import React, { useState, useEffect } from 'react';
import { corelCommunication } from '../lib/corel/communication';
import { Keys, readWithAliases } from '../lib/storage/keys';
import { logger } from '../lib/terminalLogger';
import { CloudIcon as Cloud, AlertTriangleIcon as AlertTriangle, ExternalLinkIcon as ExternalLink, CheckIcon as Check } from './icons';

interface UploadProgressProps {
  onClose: () => void;
  onRetry?: () => void;
}

type UploadState = 'uploading' | 'error' | 'success';

export function UploadProgress({ onClose, onRetry }: UploadProgressProps) {
  const [uploadState, setUploadState] = useState<UploadState>('uploading');
  const [progress, setProgress] = useState(0);
  const [filePath, setFilePath] = useState('');

  useEffect(() => {
    // Recuperar dados do storage para obter o caminho do arquivo
    try {
      const saveData = readWithAliases<any>(
        Keys.export.currentSaveData,
        ['currentSaveData'],
        { area: 'session', persistIfAliased: true }
      );
      if (saveData?.caminho_arquivo) {
        setFilePath(saveData.caminho_arquivo);
        // debugLog: arquivo para upload
      }
    } catch (error) {
      logger.error('❌ Erro ao obter caminho do arquivo para upload:', error);
    }

    if (uploadState === 'uploading') {
      // Simular progresso de upload
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setTimeout(() => {
              // Simular envio para a nuvem
              sendFileToCloudStorage();
              setUploadState('success');
              setTimeout(onClose, 1500);
            }, 500);
            return 100;
          }
          return prev + 5;
        });
      }, 300);

      return () => clearInterval(interval);
    }
  }, [uploadState, onClose]);

  const sendFileToCloudStorage = () => {
    // Enviar mensagem ao WebView para fazer upload do arquivo para armazenamento na nuvem
    if (filePath) {
      try {
        corelCommunication.sendActionGuarded('uploadArquivo', {
          caminhoArquivo: filePath,
          destino: 'googleDrive',
          metadados: {
            timestamp: new Date().toISOString(),
            usuarioUpload: localStorage.getItem('currentUser') || 'usuário externo'
          }
        } as any);
        // debugLog: upload solicitado
      } catch {}
    } else {
      logger.warn('⚠️ Caminho do arquivo não disponível ou WebView não encontrado');
    }
  };

  // Função de recuperação em caso de erro
  const handleRetry = () => {
    setProgress(0);
    setUploadState('uploading');
    if (onRetry) onRetry();
  };

  return (
    <div className="fixed inset-0 bg-navy-900/80 backdrop-blur-sm flex items-center justify-center">
      <div className="bg-navy-800 rounded-lg p-6 w-96 animate-fadeIn">
        {uploadState === 'error' ? (
          <div className="flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-500" />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">
              Falha ao enviar para o Google Drive
            </h3>
            <p className="text-gray-400 mb-3">
              Não foi possível concluir o upload do arquivo para o Google Drive.
            </p>
            <p className="text-xs text-gray-500 mb-6">
              O arquivo foi salvo localmente em: {filePath || "Caminho não disponível"}
            </p>
            <div className="flex space-x-3">
              <button
                onClick={handleRetry}
                className="px-4 py-2 bg-navy-700 text-white rounded hover:bg-navy-600 transition-colors"
              >
                Tentar Novamente
              </button>
              <button
                onClick={() => {
                  try { corelCommunication.sendActionGuarded('abrirPainelWeb', {}); } catch {}
                }}
                className="flex items-center px-4 py-2 bg-navy-700 text-white rounded hover:bg-navy-600 transition-colors"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Enviar no Painel Web
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center mb-4">
              {uploadState === 'success' ? (
                <Check className="w-6 h-6 text-emerald-500" />
              ) : (
                <Cloud className="w-6 h-6 text-blue-500" />
              )}
            </div>
            <h3 className="text-lg font-medium text-white mb-2">
              {uploadState === 'success' 
                ? 'Upload Concluído'
                : 'Enviando para o Google Drive'
              }
            </h3>
            <p className="text-gray-400 mb-2">
              {uploadState === 'success'
                ? 'Seu arquivo foi enviado com sucesso para o Google Drive.'
                : 'Aguarde enquanto fazemos o upload do seu arquivo...'
              }
            </p>
            <p className="text-xs text-gray-500 mb-4">
              {filePath ? filePath.split('\\').pop() : "Arquivo"}
            </p>
            {uploadState === 'uploading' && (
              <div className="w-full">
                <div className="h-1.5 w-full bg-navy-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 transition-all duration-300 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-sm text-gray-400 mt-2">{progress}%</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}